import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  TableContainer,
  Paper,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  TextField,
  Autocomplete,
  Grid,
} from "@mui/material";
import {
  ViewButton,
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
  StyledDialogBoxTitle,
} from "components/StyledComponents/styledComponents.js";
import { useNavigate } from "react-router-dom";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { monthEndStyles } from "components/Styles/monthEndStyles";
import { CustomPaper, autoSelectStyle } from "components/Styles/globalStyles";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";

const resolveDsaId = (dsaObj) => {
  if (!dsaObj) return undefined;
  const candidates = ["dsa_Id", "dsaId", "dsa_id", "DSA_Id", "DSAID", "id", "ID"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  const idKey = Object.keys(dsaObj).find(
    (k) => k.toLowerCase().includes("id") && !isNaN(dsaObj[k])
  );
  return idKey ? dsaObj[idKey] : undefined;
};

const resolveDsaName = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaName", "dsa_Name", "dsa_name", "DSAName", "name", "Name"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  const nameKey = Object.keys(dsaObj).find((k) => k.toLowerCase().includes("name"));
  return nameKey ? dsaObj[nameKey] : "";
};

const resolveDsaStatus = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaStatus", "dsa_Status", "dsa_status", "DSAStatus", "status", "Status"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null) return dsaObj[key];
  }
  return "";
};

ProductBusiness.propTypes = {
  productWiseBusinesByDateRange: PropTypes.string.isRequired,
  productWiseBusinessByProductId: PropTypes.string.isRequired,
  isItByDsa: PropTypes.bool,
};

export default function ProductBusiness({
  productWiseBusinesByDateRange,
  productWiseBusinessByProductId,
  isItByDsa = "",
}) {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const { monthIdData } = useBusinessMonthYear();
  const [monthId, setMonthId] = useState(0);
  const businessCodeRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const MInput = inputStyle();
  const [openDailog, setOpenDailog] = useState(false);
  const [financeName, setFinanceName] = useState("");
  const jwt_token = Cookie.get("jwt_token");
  const [providerBusinessData, setProviderBusinessData] = useState([]);
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");
  // const [startDate, setStartDate] = useState(currentMonthStart);
  // const [endDate, setEndDate] = useState(currentMonthEnd);
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const navigate = useNavigate();

  const [dsaList, setDsaList] = useState([]);
  const [selectedDsa, setSelectedDsa] = useState(null);

  const mainGridRef = useRef(null);
  const dialogGridRef = useRef(null);

  const [mainFooter, setMainFooter] = useState([]);
  const [dialogFooter, setDialogFooter] = useState([]);

  const fmtINR = (val) => {
    const n = parseFloat(val);
    return isNaN(n)
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #E59400",
      };
    }
    return null;
  };

  const computeAndPinMain = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setMainFooter([]);
      return;
    }
    const sum = (field) => rows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    setMainFooter([
      {
        businessName: "Total",
        appliedAmount: sum("appliedAmount"),
        misRevenue: sum("misRevenue"),
        disburseAmount: sum("disburseAmount"),
        actualRevenue: sum("actualRevenue"),
        dmaPayout: sum("dmaPayout"),
      },
    ]);
  }, []);

  const computeAndPinDialog = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setDialogFooter([]);
      return;
    }
    const sum = (field) => rows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    setDialogFooter([
      {
        customerName: "Total",
        appliedAmount: sum("appliedAmount"),
        disbursedAmount: sum("disbursedAmount"),
        amount: sum("amount"),
        actualRevenue: sum("actualRevenue"),
        dmaPayout: sum("dmaPayout"),
        footerRow: true,
      },
    ]);
  }, []);

  const onMainFilterChanged = useCallback(() => {
    if (!mainGridRef.current?.api) return;
    const visibleRows = [];
    mainGridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPinMain(visibleRows);
  }, [computeAndPinMain]);

  const onDialogFilterChanged = useCallback(() => {
    if (!dialogGridRef.current?.api) return;
    const visibleRows = [];
    dialogGridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPinDialog(visibleRows);
  }, [computeAndPinDialog]);

  const applyAutoWidth = (ws, sheetRows, headers) => {
    const colWidths = headers.map((h) => {
      const maxDataLen = sheetRows.reduce((max, row) => {
        const val = row[h] !== undefined && row[h] !== null ? String(row[h]) : "";
        return Math.max(max, val.length);
      }, 0);
      return { wch: Math.min(60, Math.max(10, h.length, maxDataLen)) };
    });
    ws["!cols"] = colWidths;
  };

  const handleDownload = () => {
    if (!mainGridRef.current?.api) return;
    const exportRows = [];
    mainGridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) exportRows.push(node.data);
    });
    if (!exportRows.length) return;

    const sum = (field) => exportRows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    const totalsRow = {
      customerName: "Total",
      appliedAmount: sum("appliedAmount"),
      disbursedAmount: sum("disbursedAmount"),
      amount: sum("amount"),
      actualRevenue: sum("actualRevenue"),
      dmaPayout: sum("dmaPayout"),
    };
    const exportColumns = columns.filter((c) => c.field);
    const headers = exportColumns.map((c) => c.headerName);
    const sheetRows = [...exportRows, totalsRow].map((row) => {
      const obj = {};
      exportColumns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });

    const ws = utils.json_to_sheet(sheetRows, { header: headers });
    applyAutoWidth(ws, sheetRows, headers);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Product Business");
    writeFile(wb, "Monthly_Business_Product_Data.xlsx");
  };

  const handleDialogDownload = () => {
    if (!dialogGridRef.current?.api) return;
    const exportRows = [];
    dialogGridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) exportRows.push(node.data);
    });
    if (!exportRows.length) return;

    const sum = (field) => exportRows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    const totalsRow = {
      customerName: "Total",
      loanAmount: sum("loanAmount"),
      revenue: sum("revenue"),
      dsaPayoutValue: sum("dsaPayoutValue"),
      companyRetention: sum("companyRetention"),
    };

    const exportColumns = providerBusinessColumns.filter((c) => c.field);
    const headers = exportColumns.map((c) => c.headerName);
    const sheetRows = [...exportRows, totalsRow].map((row) => {
      const obj = {};
      exportColumns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });

    const ws = utils.json_to_sheet(sheetRows, { header: headers });
    applyAutoWidth(ws, sheetRows, headers);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Product Entries");
    writeFile(wb, `${financeName}_Business_Data.xlsx`);
  };

  const fetchDsaList = async () => {
    try {
      const res = await fetch(URLs.dsaMasterGetUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch DSA list");
      const data = await res.json();
      const rawList = Array.isArray(data) ? data : data.data || [];
      setDsaList(rawList.filter((item) => resolveDsaStatus(item) === "S"));
    } catch {
      showAlert("Failed to fetch DSA list", "error");
    }
  };

  useEffect(() => {
    fetchDsaList();
  }, []);

  const fetchEntriesByMonthId = async () => {
    if (!startDate || !endDate) {
      showAlert("Please select both start and end dates", "error");
      return;
    }
    if (!selectedDsa) {
      showAlert("Please select a DSA", "error");
      return;
    }
    const dsaId = resolveDsaId(selectedDsa);
    if (dsaId === undefined || dsaId === null || dsaId === "") {
      showAlert("Selected DSA has no valid ID. Please contact support.", "error");
      return;
    }

    const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
    const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
    setLoading(true);
    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      };
      const response = await fetch(
        `${productWiseBusinesByDateRange}startDate=${formattedStartDate}&endDate=${formattedEndDate}&dsaId=${dsaId}`,
        options
      );
      const data = await response.json();
      if (response.ok) {
        setServicesData(data);
        computeAndPinMain(data);
      } else {
        throw new Error("Failed to fetch entries data");
      }
    } catch (error) {
      showAlert(`Failed to fetch data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClickOpenDailog = () => setOpenDailog(true);
  const handleCloseDailog = () => setOpenDailog(false);
  const handleCloseAlert = () => setOpen(false);

  const handleViewClick = async (rowData) => {
    const businessId = rowData.businessId;
    setFinanceName(rowData.businessTypeCode);
    handleClickOpenDailog();
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      setLoading(true);
      try {
        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        };
        const dsaId = resolveDsaId(selectedDsa);
        const url = `${productWiseBusinessByProductId}?startDate=${formattedStartDate}&endDate=${formattedEndDate}&businessTypeCode=${rowData.businessTypeCode}&dsaId=${dsaId}`;
        const response = await fetch(url, options);
        const data = await response.json();
        if (response.ok) {
          setProviderBusinessData(data);
          computeAndPinDialog(data);
        } else {
          throw new Error("Failed to fetch entries data");
        }
      } catch (error) {
        showAlert(`Failed to fetch data: ${error.message}`, "error");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (monthIdData && monthIdData.length > 0) {
      setMonthId(monthIdData[0].businessMonthID);
    }
  }, [monthIdData]);

  const ActionCellRendererProviderBusiness = (props) => {
    if (props.node.rowPinned) return null;
    const rowIndex = props.node.rowIndex;
    const rowData = props.api.getDisplayedRowAtIndex(rowIndex)?.data;
    if (!rowData) return null;
    return (
      <ViewButton type="button" onClick={() => handleViewClick(rowData)}>
        <VisibilityIcon />
      </ViewButton>
    );
  };
  ActionCellRendererProviderBusiness.propTypes = {
    node: PropTypes.shape({
      rowIndex: PropTypes.number,
      rowPinned: PropTypes.string,
    }).isRequired,
    api: PropTypes.shape({
      getDisplayedRowAtIndex: PropTypes.func.isRequired,
    }).isRequired,
  };

  const columns = [
    {
      headerName: "Product Name",
      field: "businessName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "Code",
      field: "businessTypeCode",
      filter: true,
      sortable: true,
      minWidth: 100,
      flex: 1,
    },
    {
      headerName: "DsaName",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 100,
      flex: 1,
    },
    {
      headerName: "Applied Amount",
      field: "appliedAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "MIS Revenue",
      field: "misRevenue",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Disburse Amount",
      field: "disburseAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Actual Revenue",
      field: "actualRevenue",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DMA Payout",
      field: "dmaPayout",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "View",
      cellRenderer: ActionCellRendererProviderBusiness,
      width: 80,
    },
  ];

  const providerBusinessColumns = [
    {
      headerName: "Business Type",
      field: "businessType",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
    },
    {
      headerName: "Banker Name",
      field: "bankerName",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "Application Number",
      field: "applicationNumber",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "Customer Name",
      field: "customerName",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
    },
    {
      headerName: "Provider Name",
      field: "providerName",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
    },
    {
      headerName: "DSA Name",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1,
    },
    {
      headerName: "Applied Amount",
      field: "appliedAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Disbursed Amount",
      field: "disbursedAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "MIS Revenue",
      field: "amount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Actual Revenue",
      field: "actualRevenue",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DMA Payout",
      field: "dmaPayout",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Business Status",
      field: "businessStatusLabel",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
    },
    {
      headerName: "Status",
      field: "statusLabel",
      filter: true,
      sortable: true,
      minWidth: 220,
      flex: 2,
    },
  ];

  useEffect(() => {
    computeAndPinDialog(providerBusinessData);
  }, [providerBusinessData]);

  const triggerApiCall = () => {
    businessCodeRef.current = monthId;
    fetchEntriesByMonthId();
  };

  return (
    <>
      {/* Loading dialog */}
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog
        open={openDailog}
        onClose={handleCloseDailog}
        fullScreen
        sx={{
          ".MuiDialog-paper": {
            width: "90%",
            height: "90%",
            maxWidth: "none",
            minWidth: "500px",
            margin: "auto",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle>
          <StyledDialogBoxTitle>{financeName} Business</StyledDialogBoxTitle>
          <StyledButton
            onClick={handleDialogDownload}
            sx={{ position: "absolute", right: 90, top: 8 }}
          >
            <DownloadForOfflineIcon fontSize="medium" />
          </StyledButton>
          <StyledButton
            edge="end"
            color="inherit"
            onClick={handleCloseDailog}
            aria-label="close"
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            Close
          </StyledButton>
        </DialogTitle>
        <DialogContent>
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              ref={dialogGridRef}
              rowData={providerBusinessData}
              columnDefs={providerBusinessColumns}
              pagination={true}
              paginationPageSize={10}
              pinnedBottomRowData={dialogFooter}
              getRowStyle={getRowStyle}
              onFilterChanged={onDialogFilterChanged}
              animateRows={true}
            />
          </StyledAgGridContainer>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleCloseAlert} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      {/* Main table */}
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          <Grid container spacing={2} alignItems="center" wrap="wrap" sx={{ mb: 1 }}>
            {/* Start Date */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                sx={{ ...MInput.b, maxWidth: "160px" }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                sx={{ ...MInput.b, maxWidth: "160px" }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* Select DSA */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Autocomplete
                options={dsaList}
                getOptionLabel={(option) => resolveDsaName(option)}
                value={selectedDsa}
                onChange={(_, v) => setSelectedDsa(v || null)}
                filterOptions={(options, { inputValue }) => {
                  const input = inputValue.toLowerCase();
                  return options
                    .sort((a, b) => {
                      const aName = resolveDsaName(a).toLowerCase();
                      const bName = resolveDsaName(b).toLowerCase();
                      const aS = aName.startsWith(input);
                      const bS = bName.startsWith(input);
                      return aS === bS ? 0 : aS ? -1 : 1;
                    })
                    .filter((o) => resolveDsaName(o).toLowerCase().includes(input));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select DSA"
                    variant="outlined"
                    fullWidth
                    required
                    sx={{ height: 43, ...autoSelectStyle, ...MInput.b }}
                    InputLabelProps={{ sx: { color: MInput.a } }}
                  />
                )}
                PaperComponent={CustomPaper}
              />
            </Grid>

            {/* Get button */}
            <Grid item xs={12} sm={6} md={1.5}>
              <StyledButton onClick={triggerApiCall} fullWidth>
                Get
              </StyledButton>
            </Grid>

            {/* Excel download */}
            <Grid item xs="auto">
              <StyledButton onClick={handleDownload}>
                <DownloadForOfflineIcon fontSize="medium" />
              </StyledButton>
            </Grid>
          </Grid>
        </Box>

        <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
          <AgGridReact
            ref={mainGridRef}
            rowData={servicesData}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={10}
            pinnedBottomRowData={mainFooter}
            getRowStyle={getRowStyle}
            onFilterChanged={onMainFilterChanged}
          />
        </StyledAgGridContainer>
      </TableContainer>
    </>
  );
}
